import { useCallback, useContext } from "react";
import {
  SubmitPhoneNumberProps,
  deleteUserFromDatabase,
  signIn,
  submit,
  submitPhoneNumber,
} from "../services/auth/index-new";
import { BoardingFlowContext } from "../context/auth/BoardingFlowContext";
import { castIndianTime } from "../helpers/getIndianTime";
import packageInfo from "../package.json";
import { defaultPic } from "../public/assets";
import { IntroContext, UserContext } from "../context";
import { useFormContext } from "react-hook-form";
import { useRouter } from "next/router";

interface SignInWithPhoneProps {
  phoneNumber: string;
  tempData?: firebase.User;
  setIsPending: (value: boolean) => void;
  handleSuccess?: SubmitPhoneNumberProps["handleSuccess"];
  handleError?: SubmitPhoneNumberProps["handleError"];
}

// const obj = {
//   name,
//   profile_url,
//   role,
//   email,
//   is_instructor: isInstructor,
//   sign_up_ts,
//   phone_number,
//   has_rated_app,
//   app_rating,
//   pro_expiration_date,
//   tier,
//   grade,
//   phone_country_code,
//   available_doubt_count,
// };

export interface UserObject {
  uid: string;
  name: string;
  profile_url: string;
  role: string;
  email: string;
  is_instructor: boolean;
  sign_up_ts: number;
  phone_number: string;
  has_rated_app: boolean;
  app_rating: number;
  pro_expiration_date: number;
  tier: string;
  grade: string;
  phone_country_code: string;
  available_doubt_count: number;
}

export const getUserInfoById = async (userId): Promise<UserObject> => {
  let name,
    profile_url,
    role,
    email,
    isInstructor,
    sign_up_ts,
    phone_number,
    has_rated_app,
    app_rating,
    pro_expiration_date,
    tier,
    uid,
    grade,
    available_doubt_count,
    phone_country_code;

  await require("../firebase-config")
    .db.collection("users")
    .doc(userId)
    .get()
    .then(async (doc) => {
      if (doc.exists) {
        name = doc.data().name;
        grade = doc.data().grade;
        profile_url =
          doc.data().profile_url && doc.data().profile_url.trim().length > 0
            ? doc.data().profile_url
            : defaultPic;
        role = doc.data().role;
        email = doc.data().email;
        isInstructor = doc.data().is_instructor;
        sign_up_ts = doc.data().sign_up_ts;
        phone_number = doc.data().phone_number;
        phone_country_code = doc.data().phone_country_code;
        has_rated_app = doc.data().has_rated_app;
        app_rating = doc.data().app_rating;
        pro_expiration_date = doc.data().pro_expiration_date;
        tier = doc.data().tier;
        available_doubt_count = doc.data().available_doubt_count;
      } else {
        console.log("No user found");
      }
    })
    .catch((er) => {
      // console.log(er);
      name = null;
      grade = null;
      profile_url = null;
      role = null;
      email = null;
      isInstructor = null;
      sign_up_ts = null;
      phone_number = null;
      phone_country_code = null;
      has_rated_app = null;
      app_rating = null;
      pro_expiration_date = null;
      tier = null;
      available_doubt_count = 0;
    });

  return {
    name,
    profile_url,
    role,
    email,
    is_instructor: isInstructor,
    sign_up_ts,
    phone_number,
    has_rated_app,
    app_rating,
    pro_expiration_date,
    tier,
    grade,
    phone_country_code,
    available_doubt_count,
    uid: userId,
  };
};

export const uploadUserInfo = async ({
  userId,
  name,
  email = null,
  avatarUrl,
  isInstructor,
  phoneNumber = null,
  countryCode,
}: {
  userId: string;
  name: string | null;
  email: string | null;
  avatarUrl: string;
  isInstructor: boolean;
  phoneNumber: string | null;
  countryCode: string;
}): Promise<UserObject> => {
  let time_now = await castIndianTime();

  const objToSet = {
    app_rating: null,
    app_rating_history: [],
    email: email,
    grade: null,
    has_rated_app: false,
    is_instructor: isInstructor,
    name: name,
    phone_country_code: countryCode,
    phone_number: phoneNumber ? phoneNumber : null,
    pro_expiration_date: null,
    profile_url:
      avatarUrl && avatarUrl.trim().length > 0 ? avatarUrl : defaultPic,
    role: isInstructor ? "PuStack Faculty" : "Student", //TODO change as required
    sign_up_ts: +time_now,
    tier: "free",
    available_doubt_count: 1,
    uid: userId,
    source: "web " + packageInfo.version,
  };

  await require("../firebase-config")
    .db.collection("users")
    .doc(userId)
    .set(objToSet)
    .then(() => console.log("done1"))
    .catch((err) => console.log(err));

  await require("../firebase-config")
    .db.collection("users")
    .doc(userId)
    .collection("meta")
    .doc(userId)
    .set({
      name: name,
      profile_url:
        avatarUrl && avatarUrl.trim().length > 0 ? avatarUrl : defaultPic,
      role: isInstructor ? "PuStack Faculty" : "Student",
    })
    .then(() => console.log("done2"))
    .catch((err) => console.log(err));

  return objToSet;
};

export const useBoardingFlow = () => {
  const [tempData, setTempData] = useContext(BoardingFlowContext).tempData;
  const [, setPhoneExists] = useContext(BoardingFlowContext).phoneExists;
  const [, setPhoneRoute] = useContext(BoardingFlowContext).phoneRoute;
  const [facebookPendingCredentials] =
    useContext(BoardingFlowContext).facebookPendingCredentials;
  const [googleMailAddress] =
    useContext(BoardingFlowContext).pendingEmailAddress;
  const authJourneyRef = useContext(IntroContext).authJourneyRef;
  const { getValues } = useFormContext();
  const [, setUser] = useContext(UserContext).user;
  const [, setIsUserProTier] = useContext(UserContext).tier;
  const [, setIsInstructor] = useContext(UserContext).isInstructor;
  const [, setActiveTab] = useContext(BoardingFlowContext).activeTab;
  const [, setShowLandingPage] = useContext(IntroContext).showLandingPage;
  const router = useRouter();
  const [, setOpenSlider] = useContext(BoardingFlowContext).openSlider;

  const signInWithPhone = useCallback(
    async ({
      phoneNumber,
      setIsPending,
      handleSuccess = () => {},
      handleError = () => {},
    }: SignInWithPhoneProps) => {
      require("../firebase-config").auth.useDeviceLanguage();

      if (tempData && tempData.phoneNumber !== null) {
        console.log(
          "Sign in with phone | Temp Data found, so deleting... - ",
          tempData
        );
        tempData.delete();
        deleteUserFromDatabase(tempData?.uid);
        setTempData(null);
        setPhoneExists(true);
      } else {
        console.log("Sign in with phone | Temp Data not found");
      }

      await submitPhoneNumber({
        phoneNumber,
        handleSuccess,
        handleError,
        setIsPending,
      });
    },
    [tempData, setTempData, setPhoneExists]
  );

  const onSignInWithAppleBtnClick = useCallback(async () => {
    // setProcessStarted(true);
    setPhoneRoute(false);

    return await signIn(
      require("../firebase-config").appleProvider,
      facebookPendingCredentials,
      googleMailAddress
    );
  }, [facebookPendingCredentials, googleMailAddress, setPhoneRoute]);

  const onSignInWithGoogleBtnClick = async () => {
    // setProcessStarted(true);
    console.log("Google sign in clicked and phone route is setting to false");
    setPhoneRoute(false);

    return signIn(
      require("../firebase-config").googleProvider,
      facebookPendingCredentials,
      googleMailAddress
    );
  };

  const loginUser = useCallback(
    (data: UserObject) => {
      setTimeout(() => {
        localStorage.setItem(
          "user",
          JSON.stringify({
            uid: data?.uid,
            grade: data?.grade,
            name: data?.name,
            profile_url: data?.profile_url,
          })
        );
        localStorage.setItem(
          "isInstructor",
          JSON.stringify(data.is_instructor)
        );
        localStorage.setItem("isUserPro", JSON.stringify(data.tier !== "free"));
        setUser(data);
        setIsUserProTier(data.tier !== "free");
        setIsInstructor(data.is_instructor);
        setShowLandingPage(false);
        // handleClose();
        // loadingWrapper();let urlToSend =
        let urlToSend = "/";

        if (localStorage.getItem("guest_grade") !== data.grade) {
          return router.replace("/");
        }

        if (urlToSend.includes("guest_classroom")) {
          urlToSend = urlToSend.replace("guest_classroom", "auth_classroom");
        }

        if (urlToSend.includes("guest_tips")) {
          urlToSend = urlToSend.replace("guest_tips", "tips");
        }

        if (urlToSend.includes("guest_practice")) {
          urlToSend = urlToSend.replace("guest_practice", "practice");
        }

        router.replace(urlToSend);
      }, 2000);
      return setTimeout(() => setOpenSlider(false), 1500);
    },
    [
      setUser,
      setIsUserProTier,
      setIsInstructor,
      setShowLandingPage,
      router,
      setOpenSlider,
    ]
  );

  const setUserGradeAndName = useCallback(
    async (props?: {
      setIsPending?: React.Dispatch<React.SetStateAction<boolean>>;
    }) => {
      if (!getValues("name")) throw new Error("Name is required");
      if (!getValues("grade")) throw new Error("Grade is required");

      props?.setIsPending && props?.setIsPending(true);
      const currentUser = require("../firebase-config").auth.currentUser;
      let dataToSet = {
        uid: currentUser.uid,
        name: getValues("name"),
        grade: getValues("grade"),
      };
      const ist = await castIndianTime();

      await require("../firebase-config")
        .db.collection("users")
        .doc(dataToSet.uid)
        .set(
          {
            grade: dataToSet.grade,
            name: dataToSet.name?.trim(),
            sign_up_ts: +ist,
          },
          { merge: true }
        );

      authJourneyRef.current.logEvent("pustack_event_has_selected_grade");

      const snapshot = await require("../firebase-config")
        .db.collection("users")
        .doc(dataToSet.uid)
        .get();

      props?.setIsPending && props?.setIsPending(false);

      const userData = snapshot.data() as UserObject;

      if (
        snapshot.exists &&
        userData.grade &&
        userData.name &&
        userData.email &&
        userData.phone_number
      ) {
        return loginUser(userData);
      }

      throw new Error("User data is not complete");
    },
    [authJourneyRef, getValues, loginUser]
  );

  // const setData = async ({ user }: { user: firebase.User }) => {
  //   if (user?.email) {
  //     let userData = await getUserInfoById(user.uid);

  //     // If the user doesn't have email, then ask for it
  //     if (!userData.email || !userData.phone_number) {
  //       userData = await uploadUserInfo({
  //         userId: user.uid,
  //         name: user.displayName,
  //         email: user.email,
  //         avatarUrl: user?.photoURL ?? userData.profile_url,
  //         isInstructor: false,
  //         phoneNumber: userData.phone_number,
  //         countryCode: userData.phone_country_code,
  //       });

  //       return setActiveTab((c) => c + 1);
  //     }

  //     loginUser(userData);
  //   }
  // };

  return {
    signInWithPhone,
    onSignInWithAppleBtnClick,
    onSignInWithGoogleBtnClick,
    loginUser,
    setUserGradeAndName,
  };
};
